import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Trash2, Loader2, AlertTriangle, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { key: "alunos", label: "Alunos com vínculo quebrado", field: "alunos_orfaos", elimina: true },
  { key: "avaliacoes", label: "Avaliações práticas órfãs", field: "avaliacoes", elimina: true },
  { key: "formativas", label: "Avaliações formativas órfãs", field: "formativas", elimina: true },
  { key: "links", label: "Links de inscrição órfãos", field: "links", elimina: true },
  { key: "ocorrencias", label: "Ocorrências órfãs", field: "ocorrencias", elimina: true },
  { key: "presencas", label: "Presenças órfãs (capacitados)", field: "presencas", elimina: true },
];

export default function LimpezaAdmin() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [aberto, setAberto] = useState({});
  const [dialog, setDialog] = useState(false);
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orfaos/scan");
      setScan(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleSel = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const toggleAberto = (k) => setAberto((s) => ({ ...s, [k]: !s[k] }));

  const selecionadas = CATS.filter((c) => sel[c.key] && (scan?.resumo?.[c.field] || scan?.[c.field]?.length));
  const totalSel = selecionadas.reduce((acc, c) => acc + (scan?.[c.field]?.length || 0), 0);

  const abrirConfirmacao = () => {
    if (!selecionadas.length) { toast.error("Selecione ao menos uma categoria."); return; }
    setConfirmTxt(""); setDialog(true);
  };

  const executar = async () => {
    setBusy(true);
    try {
      const body = { confirm: confirmTxt };
      CATS.forEach((c) => { body[c.key] = !!sel[c.key]; });
      const { data } = await api.post("/admin/orfaos/limpar", body);
      const x = data.excluidos;
      toast.success(`Órfãos removidos: ${x.alunos} aluno(s), ${x.avaliacoes} avaliação(ões), ${x.formativas} formativa(s), ${x.links} link(s), ${x.ocorrencias} ocorrência(s), ${x.presencas} presença(s).`);
      setDialog(false); setSel({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const r = scan?.resumo || {};
  const totalElim = r.total_elimina_veis || 0;

  return (
    <div>
      <PageHeader title="Limpeza Administrativa" subtitle="Varredura e remoção segura de registros órfãos">
        <Button data-testid="rescan-orfaos-btn" onClick={load} disabled={loading} variant="outline">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Verificar registros órfãos
        </Button>
      </PageHeader>

      {!scan && loading && <p className="text-muted-foreground text-sm">Analisando o banco…</p>}

      {scan && (
        <div className="space-y-4">
          <Card className="p-4 bg-card border-border">
            <div className="flex items-start gap-3">
              {totalElim === 0
                ? <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0" />
                : <AlertTriangle className="h-6 w-6 text-amber-400 shrink-0" />}
              <div className="text-sm">
                {totalElim === 0
                  ? <p data-testid="orfaos-limpo" className="text-emerald-300 font-medium">Nenhum registro órfão encontrado. A base está íntegra.</p>
                  : <p className="text-foreground"><b>{totalElim}</b> registro(s) órfão(s) elimináveis encontrados.</p>}
                {r.alunos_sem_vinculo > 0 && (
                  <p className="text-muted-foreground mt-1">
                    <b>{r.alunos_sem_vinculo}</b> aluno(s) sem vínculo de turma — <b>não</b> são órfãos (cadastro legítimo aguardando vinculação). Não serão excluídos.
                  </p>
                )}
              </div>
            </div>
          </Card>

          {CATS.map((c) => {
            const itens = scan[c.field] || [];
            const n = itens.length;
            return (
              <Card key={c.key} data-testid={`cat-${c.key}`} className="p-4 bg-card border-border">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Checkbox data-testid={`sel-cat-${c.key}`} checked={!!sel[c.key]} disabled={n === 0}
                      onCheckedChange={() => toggleSel(c.key)} />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{c.label}</div>
                      <div className="text-xs text-muted-foreground">{n} registro(s)</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={n ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-secondary text-secondary-foreground border-border"}>{n}</Badge>
                    {n > 0 && (
                      <button data-testid={`toggle-${c.key}`} onClick={() => toggleAberto(c.key)} className="text-muted-foreground hover:text-foreground">
                        <ChevronDown className={`h-4 w-4 transition-transform ${aberto[c.key] ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                </div>
                {aberto[c.key] && n > 0 && (
                  <div className="mt-3 border-t border-border pt-3 space-y-1 max-h-64 overflow-y-auto text-xs font-mono">
                    {itens.map((it, i) => (
                      <div key={it.id || it.token || i} className="flex flex-wrap items-center gap-x-2 text-muted-foreground">
                        <span className="text-foreground">{it.nome || it.comportamento || (it.id ? it.id.slice(0, 8) : (it.token || "").slice(0, 8))}</span>
                        {it.matricula ? <span>· {it.matricula}</span> : null}
                        <span className="text-amber-300/80">· {it.motivo}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}

          {r.alunos_sem_vinculo > 0 && (
            <Card className="p-4 bg-card border-border opacity-80">
              <div className="text-sm font-medium">Alunos sem vínculo (preservados)</div>
              <div className="text-xs text-muted-foreground mt-1">{r.alunos_sem_vinculo} cadastro(s) sem turma. Podem estar aguardando vinculação — não entram na limpeza automática.</div>
              <div className="mt-2 space-y-1 text-xs font-mono">
                {(scan.alunos_sem_vinculo || []).map((a) => (
                  <div key={a.id} className="text-muted-foreground"><span className="text-foreground">{a.nome}</span> {a.matricula ? `· ${a.matricula}` : ""}</div>
                ))}
              </div>
            </Card>
          )}

          {totalElim > 0 && (
            <div className="flex justify-end">
              <Button data-testid="limpar-orfaos-btn" variant="destructive" onClick={abrirConfirmacao} disabled={!selecionadas.length}>
                <Trash2 className="h-4 w-4 mr-2" />Limpar selecionados ({totalSel})
              </Button>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={dialog} onOpenChange={(v) => { if (!v) setDialog(false); }}>
        <AlertDialogContent className="bg-popover border-border max-w-lg" data-testid="limpeza-orfaos-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-400" />Excluir registros órfãos</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Ação <b>definitiva</b>. Serão removidos apenas os registros órfãos das categorias selecionadas:</p>
                <div className="rounded-lg border border-border bg-secondary/20 p-3 font-mono text-xs space-y-1">
                  {selecionadas.map((c) => (
                    <div key={c.key}>{c.label}: <b className="text-foreground">{scan?.[c.field]?.length || 0}</b></div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Modelos oficiais, instrução oficial, bancos de critérios (PI e C-Ext-NA), contas administrativas e turmas válidas são preservados.</p>
                <div>
                  <p className="mb-1">Para confirmar, digite <b>EXCLUIR</b>:</p>
                  <Input data-testid="confirm-orfaos-input" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="EXCLUIR" autoFocus />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orfaos-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="orfaos-confirmar" disabled={confirmTxt.trim().toUpperCase() !== "EXCLUIR" || busy}
              onClick={(e) => { e.preventDefault(); executar(); }} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
